import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, DollarSign, Percent, Plus, Trash2, Check, RefreshCw, ArrowRightLeft, Shield, Clock, Mail, Eye, EyeOff } from 'lucide-react';
import { HelpTip } from '../WMSShared';
import SecurityTab from './SecurityTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';
const AUTO = process.env.REACT_APP_BACKEND_URL + '/api/wms/automation';

export default function SettingsTab({ onConfigChange, user }) {
  const [settingsTab, setSettingsTab] = useState(() => localStorage.getItem('wms_settings_tab') || 'config');
  const [config, setConfig]   = useState({
    currency_code: 'SGD', currency_symbol: 'S$',
    currency_presets: [], tax_rates: [],
    timezone: 'Asia/Singapore',
  });
  const [fxData, setFxData]   = useState({ rates: {}, fetched_at: '', source: '', status: '' });
  const [saving, setSaving]   = useState({});
  const [saved, setSaved]     = useState({});
  const [newTax, setNewTax]   = useState({ name: '', rate: '' });
  const [smtp, setSmtp]       = useState({ host: '', port: 465, username: '', password: '', use_tls: true, from_email: '' });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const load = async () => {
    try {
      const [cfgRes, fxRes] = await Promise.all([
        axios.get(`${API}/settings/config`),
        axios.get(`${API}/settings/exchange-rates`),
      ]);
      setConfig(cfgRes.data);
      setFxData(fxRes.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const loadSmtp = async () => {
    try { const r = await axios.get(`${AUTO}/smtp`); setSmtp(r.data); } catch {}
  };
  useEffect(() => { if (settingsTab === 'email') loadSmtp(); }, [settingsTab]);

  const saveSmtp = async () => {
    setSmtpLoading(true); setSmtpMsg(null);
    try {
      await axios.put(`${AUTO}/smtp`, smtp);
      setSmtpMsg({ type: 'success', text: 'SMTP settings saved' });
      loadSmtp();
    } catch (e) { setSmtpMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed' }); }
    setSmtpLoading(false);
  };

  const testSmtp = async () => {
    if (!testEmail) return;
    setSmtpMsg(null);
    try {
      const r = await axios.post(`${AUTO}/smtp/test`, { email: testEmail });
      setSmtpMsg({ type: 'success', text: r.data.message });
    } catch (e) { setSmtpMsg({ type: 'error', text: e.response?.data?.detail || 'Test failed' }); }
  };

  const save = async (key, payload) => {
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await axios.put(`${API}/settings/config`, payload);
      await load();
      if (onConfigChange) onConfigChange();
      setSaved(p => ({ ...p, [key]: true }));
      setTimeout(() => setSaved(p => ({ ...p, [key]: false })), 2000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
    setSaving(p => ({ ...p, [key]: false }));
  };

  const setCurrency = async (preset) => {
    await save('currency', { currency_code: preset.code, currency_symbol: preset.symbol });
    setConfig(p => ({ ...p, currency_code: preset.code, currency_symbol: preset.symbol }));
  };
  const addTaxRate = async () => {
    if (!newTax.name.trim() || newTax.rate === '') return;
    const rate = parseFloat(newTax.rate);
    if (isNaN(rate) || rate < 0 || rate > 100) { alert('Rate must be 0–100'); return; }
    const updated = [...config.tax_rates, {
      id: `custom_${Date.now()}`, name: newTax.name.trim(), rate
    }];
    await save('tax', { tax_rates: updated });
    setNewTax({ name: '', rate: '' });
  };

  const deleteTaxRate = async (id) => {
    if (config.tax_rates.length <= 1) { alert('Keep at least one tax rate.'); return; }
    const updated = config.tax_rates.filter(t => t.id !== id);
    await save('tax', { tax_rates: updated });
  };

  const SaveButton = ({ k }) => (
    <button disabled={saving[k]}
      className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${saved[k] ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>
      {saved[k] ? <><Check className="w-3.5 h-3.5" />Saved!</> : saving[k] ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving...</> : 'Save'}
    </button>
  );

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Settings</h2>
        <p className="text-sm text-gray-500 mt-1">Configure currency, tax rates, and security.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-3">
        {[['config', Settings, 'Configuration'], ['email', Mail, 'Email / SMTP'], ['security', Shield, 'Security']].map(([id, Icon, label]) => (
          <button key={id} onClick={() => { setSettingsTab(id); localStorage.setItem('wms_settings_tab', id); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${settingsTab === id ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {settingsTab === 'security' && <SecurityTab user={user} />}

      {settingsTab === 'email' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="font-semibold flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              SMTP Configuration
              <HelpTip text="Configure your outgoing email server for sending POs to suppliers, security alerts, and digest reports." />
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Used for PO emails, 2FA setup, agent alerts, and scheduled digests.</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">SMTP Host</label>
                <input type="text" value={smtp.host} placeholder="smtp.gmail.com"
                  onChange={e => setSmtp(p => ({ ...p, host: e.target.value }))}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                  data-testid="smtp-host" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Port</label>
                <input type="number" value={smtp.port} placeholder="465"
                  onChange={e => setSmtp(p => ({ ...p, port: parseInt(e.target.value) || 465 }))}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                  data-testid="smtp-port" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Username / Email</label>
              <input type="email" value={smtp.username} placeholder="you@gmail.com"
                onChange={e => setSmtp(p => ({ ...p, username: e.target.value }))}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                data-testid="smtp-username" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Password / App Password</label>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} value={smtp.password} placeholder="App password"
                  onChange={e => setSmtp(p => ({ ...p, password: e.target.value }))}
                  className="w-full mt-1 border rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                  data-testid="smtp-password" />
                <button onClick={() => setShowPass(!showPass)} className="absolute right-2 top-3 text-gray-400 hover:text-gray-700">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">From Email (optional)</label>
              <input type="email" value={smtp.from_email} placeholder="Same as username if blank"
                onChange={e => setSmtp(p => ({ ...p, from_email: e.target.value }))}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                data-testid="smtp-from" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={smtp.use_tls}
                onChange={e => setSmtp(p => ({ ...p, use_tls: e.target.checked }))}
                className="rounded border-gray-300" />
              <span className="text-sm text-gray-700">Use TLS/SSL</span>
            </label>

            {smtpMsg && (
              <div className={`text-sm px-4 py-2 rounded-lg ${smtpMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {smtpMsg.text}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button onClick={saveSmtp} disabled={smtpLoading}
                className="flex items-center gap-1 px-5 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-all">
                {smtpLoading ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving...</> : <><Check className="w-3.5 h-3.5" />Save SMTP</>}
              </button>
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Test SMTP</p>
              <div className="flex gap-2">
                <input type="email" placeholder="your@email.com" value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none"
                  data-testid="smtp-test-email" />
                <button onClick={testSmtp}
                  className="text-sm border border-blue-200 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-50 font-medium"
                  data-testid="smtp-test-btn">
                  Send Test Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {settingsTab === 'config' && <>
      {/* ── CURRENCY ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            Currency
            <HelpTip text="Sets the currency symbol used throughout the WMS — in order totals, purchase orders, reports, and PDFs." />
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Currently: <strong>{config.currency_code}</strong> ({config.currency_symbol})</p>
        </div>
        <div className="p-6 space-y-4">
          {/* Quick select presets */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Select currency</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {config.currency_presets.map(preset => (
                <button key={preset.code} onClick={() => setCurrency(preset)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all ${
                    config.currency_code === preset.code
                      ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}>
                  <span className="text-base font-bold w-6 text-center">{preset.symbol}</span>
                  <div className="text-left">
                    <p className="text-xs font-medium leading-tight">{preset.code}</p>
                    <p className="text-[10px] text-gray-400 leading-tight">{preset.name}</p>
                  </div>
                  {config.currency_code === preset.code && <Check className="w-3.5 h-3.5 text-blue-600 ml-auto" />}
                </button>
              ))}
            </div>
          </div>

          {/* Custom symbol override */}
          <div className="border-t pt-4">
            <label className="text-xs font-medium text-gray-600 mb-2 block">Or set a custom symbol</label>
            <div className="flex gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Code</span>
                <input value={config.currency_code}
                  onChange={e => setConfig(p => ({ ...p, currency_code: e.target.value.toUpperCase() }))}
                  className="pl-12 pr-3 py-2 border rounded-lg text-sm w-24" maxLength={3} />
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Symbol</span>
                <input value={config.currency_symbol}
                  onChange={e => setConfig(p => ({ ...p, currency_symbol: e.target.value }))}
                  className="pl-14 pr-3 py-2 border rounded-lg text-sm w-28" maxLength={4} />
              </div>
              <button onClick={() => save('currency', { currency_code: config.currency_code, currency_symbol: config.currency_symbol })}
                disabled={saving.currency}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${saved.currency ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'} disabled:opacity-50`}>
                {saved.currency ? <><Check className="w-3.5 h-3.5" />Saved!</> : saving.currency ? 'Saving...' : 'Apply'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Example: Code=HKD, Symbol=HK$ — then all prices show as HK$150</p>
          </div>
        </div>
      </div>

      {/* ── TIMEZONE ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-600" />
            Timezone
            <HelpTip text="Sets the timezone for all date and time displays throughout the WMS — orders, audits, products, Shopee sync timestamps." />
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Currently: <strong>{config.timezone}</strong>
          </p>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {[
              { tz: 'Asia/Singapore',    label: 'SGT',    name: 'Singapore (UTC+8)' },
              { tz: 'Asia/Hong_Kong',    label: 'HKT',    name: 'Hong Kong (UTC+8)' },
              { tz: 'Asia/Kuala_Lumpur', label: 'MYT',    name: 'Kuala Lumpur (UTC+8)' },
              { tz: 'Asia/Tokyo',        label: 'JST',    name: 'Tokyo (UTC+9)' },
              { tz: 'UTC',               label: 'UTC',    name: 'UTC (UTC+0)' },
              { tz: 'Europe/London',     label: 'GMT/BST', name: 'London (UTC+0/+1)' },
              { tz: 'America/New_York',  label: 'EST/EDT', name: 'New York (UTC-5/-4)' },
              { tz: 'America/Los_Angeles', label: 'PST/PDT', name: 'Los Angeles (UTC-8/-7)' },
              { tz: 'Australia/Sydney',  label: 'AEST',   name: 'Sydney (UTC+10/+11)' },
            ].map(({ tz, label, name }) => (
              <button key={tz} onClick={() => save('timezone', { timezone: tz })}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all ${
                  config.timezone === tz
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}>
                <span className="text-xs font-bold w-14 text-left font-mono">{label}</span>
                <span className="text-xs text-gray-600 text-left">{name}</span>
                {config.timezone === tz && <Check className="w-3.5 h-3.5 text-emerald-600 ml-auto" />}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400">
            Current time: <strong>{new Date().toLocaleString('en-SG', { timeZone: config.timezone, hour12: false })}</strong> in {config.timezone}
          </p>
        </div>
      </div>

      {/* ── TAX RATES ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
          <h3 className="font-semibold flex items-center gap-2">
            <Percent className="w-4 h-4 text-amber-600" />
            Tax Rates
            <HelpTip text="These rates appear in the GST/Tax dropdown when creating orders and receiving stock. Add any tax rates applicable to your business (GST, VAT, SST, etc.)." />
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">These rates appear in the tax dropdown in orders and purchases</p>
        </div>
        <div className="p-6 space-y-3">
          {/* Existing tax rates */}
          {config.tax_rates.map(t => (
            <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border bg-gray-50">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${t.rate === 0 ? 'bg-gray-200 text-gray-600' : 'bg-amber-100 text-amber-700'}`}>
                {t.rate}%
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs text-gray-400">Applied as {t.rate}% on the pre-tax subtotal</p>
              </div>
              <button onClick={() => deleteTaxRate(t.id)}
                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors" title="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          {/* Add new tax rate */}
          <div className="flex gap-2 pt-2 border-t">
            <input type="text" placeholder="Name, e.g. VAT 10% (HK)"
              value={newTax.name} onChange={e => setNewTax(p => ({ ...p, name: e.target.value }))}
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <div className="relative">
              <input type="number" placeholder="Rate" min="0" max="100" step="0.1"
                value={newTax.rate} onChange={e => setNewTax(p => ({ ...p, rate: e.target.value }))}
                className="w-24 border rounded-lg px-3 py-2 text-sm pr-6 focus:outline-none focus:ring-2 focus:ring-amber-400" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
            </div>
            <button onClick={addTaxRate} disabled={!newTax.name || newTax.rate === ''}
              className="flex items-center gap-1 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" />Add
            </button>
          </div>
          <p className="text-xs text-gray-400">Examples: VAT 5%, SST 6% (MY), No Tax (0%), Import Duty 10%</p>
        </div>
      </div>

      {/* ── EXCHANGE RATES ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
          <h3 className="font-semibold flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-emerald-600" />
            Exchange Rates
            <HelpTip text="Live rates fetched from open.er-api.com (free, no API key). Used automatically in the Invoice Currency and Order Currency dropdowns. Cached for 24 hours." />
          </h3>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-xs text-gray-500">
              Source: <strong>{fxData.source || '—'}</strong> ·
              Status: <span className={`font-medium ${fxData.status === 'live' ? 'text-emerald-600' : 'text-amber-600'}`}>{fxData.status || '—'}</span> ·
              Updated: {fxData.fetched_at ? new Date(fxData.fetched_at).toLocaleString() : '—'}
            </p>
            <button onClick={async () => {
              setSaving(p => ({ ...p, fx: true }));
              try { const r = await axios.post(`${API}/settings/exchange-rates/refresh`); setFxData(r.data); } catch {}
              setSaving(p => ({ ...p, fx: false }));
            }} disabled={saving.fx}
              className="flex items-center gap-1 text-xs border px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${saving.fx ? 'animate-spin' : ''}`} />
              {saving.fx ? 'Fetching...' : 'Refresh Now'}
            </button>
          </div>
        </div>
        <div className="p-6">
          {Object.keys(fxData.rates).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(fxData.rates).sort().map(([code, rate]) => {
                const baseSym = config.currency_symbol || 'S$';
                const baseCode = config.currency_code || 'SGD';
                const rateToBase = fxData.rates[baseCode] ? fxData.rates[baseCode] / rate : 1;
                return (
                  <div key={code} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${code === baseCode ? 'border-emerald-300 bg-emerald-50' : 'border-gray-100 bg-gray-50'}`}>
                    <span className={`font-medium ${code === baseCode ? 'text-emerald-700' : 'text-gray-700'}`}>{code}</span>
                    <span className="text-xs text-gray-500">
                      1 {code} = {baseSym}{rateToBase.toFixed(code === 'IDR' || code === 'VND' || code === 'KRW' ? 4 : 4)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <ArrowRightLeft className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Click "Refresh Now" to fetch live rates</p>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">Rates auto-refresh every 24 hours. Click Refresh Now for immediate update.</p>
        </div>
      </div>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-blue-800">
        <p className="font-semibold mb-2">How these settings apply</p>
        <ul className="space-y-1 text-xs text-blue-700 list-disc list-inside">
          <li><strong>Currency</strong>: changes the symbol shown next to every price in the WMS (orders, POs, reports, PDFs)</li>
          <li><strong>Tax rates</strong>: appear in the tax dropdown when creating orders or receiving stock</li>
          <li>Changes take effect immediately — refresh the page to see them across all tabs</li>
        </ul>
      </div>
      </>}
    </div>
  );
}
